import * as z from 'zod';

import { addressQuerySchema } from '@/_containers/inputs/address/shared/address-query-schema';
import { contactLinkerWithPhoneNumberQuerySchema } from '@/_pages/contacts/shared/schemas/contact-query-linker-with-phone-numbers-schema';
import { domainQueryLinkerSchemaWithExData } from '@/_pages/domains/shared/schemas';
import { billingTermQueryLinkerSchema } from '@/_pages/settings/billing-terms/shared/billing-term-query-linker-schema';
import { emailHistorySourceTypeSchema } from '@/_pages/settings/email-history/shared/schemas/email-history-query-schema';
import { agreementQueryLinkerSchema } from '@/_schemas/agreements/agreements-query-linker-schema';

import { invoiceEmailDeliveryStatusSchema } from '../invoice-email-delivery-status-schema';

export const priceFrequencyQuerySchema = z.object({
  id: z.number(),
  name: z.string(),
});

export type PriceFrequencyQuerySchema = z.infer<typeof priceFrequencyQuerySchema>;

export const invoiceAgreementDeliverableQuerySchema = z.object({
  id: z.number(),
  sku: z.string().nullable(),
  product_name: z.string().nullable(),
  price_frequency_id: z.number().nullable(),
  quantity: z.number().nullable(),
  price: z.number().nullable(),
  client_opt: z.number().nullable(),
  opted_in: z.number().nullable(),
  agreement_id: z.number().nullable(),
  price_frequency: priceFrequencyQuerySchema.nullish(),
  /** Domains for the line item (API may return a string or list). */
  domains: z.union([z.string(), z.array(z.string())]).nullish(),
});

export type InvoiceAgreementDeliverableQuerySchema = z.infer<typeof invoiceAgreementDeliverableQuerySchema>;

export const invoiceAgreementQuerySchema = agreementQueryLinkerSchema
  .omit({
    agreement_date: true,
    annual_recurring_total: true,
    expiry_date: true,
    monthly_recurring_total: true,
    opt_in_total: true,
    pipeline: true,
    status: true,
  })
  .extend({
    purchase_order: z.string().nullable().nullish(),
    billing_term: billingTermQueryLinkerSchema.nullable().nullish(),
    /** Agreement billing contact(s) — API returns an array (same as agreements module). */
    billing_contact: z.array(contactLinkerWithPhoneNumberQuerySchema).nullish(),
    billing_email_contacts: z.array(contactLinkerWithPhoneNumberQuerySchema).nullish(),
    primary_domains: z.array(domainQueryLinkerSchemaWithExData).nullish(),
    agreement_deliverables: z.array(invoiceAgreementDeliverableQuerySchema).nullish(),
    addresses: z.array(addressQuerySchema).nullish(),
  });

export type InvoiceAgreementQuerySchema = z.infer<typeof invoiceAgreementQuerySchema>;

export const invoiceAgreementLinkQuerySchema = z.object({
  id: z.number(),
  agreement_id: z.number().nullish(),
  agreement: invoiceAgreementQuerySchema.nullish(),
  invoice_deliverables: z.array(invoiceAgreementDeliverableQuerySchema).nullish(),
});

export type InvoiceAgreementLinkQuerySchema = z.infer<typeof invoiceAgreementLinkQuerySchema>;

/** Latest email history summary on invoice list/detail rows. */
export const invoiceLatestEmailHistorySchema = z.object({
  id: z.number(),
  source_id: z.number(),
  source_type: emailHistorySourceTypeSchema,
  delivery_status: invoiceEmailDeliveryStatusSchema,
  scheduled_at: z.string().nullable().optional(),
  sent_at: z.string().nullable(),
});

export type InvoiceLatestEmailHistorySchema = z.infer<typeof invoiceLatestEmailHistorySchema>;
